import { CHANNELS } from "../shared/channels";
import { CHALLENGE_POINTS, ChallengeState } from "../shared/achievements";
import { call, mediaSrc } from "./bridge";
import { flashToast } from "./dom";
import { burst } from "./juice";
import { iconSvg } from "./icons";
import { t } from "./i18n";
import { localDay } from "./awards_store";
import { pickImage } from "./image_pick";
import { openLightbox } from "./lightbox";
import { onViewShown, showView } from "./navigation";
import { GESTURE_PRESETS, startGesture } from "./gesture_launch";

// draw today: a daily exercise in the style of the drawabox drills and other warm ups, or draw a reference from three suggested.
// finishing pays points straight away, and sharing the drawing on the upskilling feed pays more. it is mounted on home and on its own page

let state: ChallengeState | null = null;
let busy = false;
const mounts: Array<() => void> = [];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = "", text = ""): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function button(text: string, className: string, act: () => void): HTMLButtonElement {
  const node = el("button", className, text);
  node.type = "button";
  node.addEventListener("click", act);
  return node;
}

function problem(error: unknown): void {
  flashToast(String(error instanceof Error ? error.message : error).replace(/^.*Error: /, ""));
}

function repaintAll(): void {
  mounts.forEach((paint) => paint());
}

async function act(action: string, value = "", extra?: unknown): Promise<boolean> {
  if (busy) return false;
  busy = true;
  try {
    const wasDone = state?.done;
    state = await call<ChallengeState>(CHANNELS.challengeAct, localDay(), action, value, extra);
    if (!wasDone && state?.done) burst(innerWidth / 2, innerHeight / 3, "draw");
    repaintAll();
    return true;
  } catch (error) {
    problem(error);
    return false;
  } finally {
    busy = false;
  }
}

export async function loadChallenge(): Promise<void> {
  try {
    state = await call<ChallengeState>(CHANNELS.challengeGet, localDay());
    repaintAll();
  } catch {
    // the module stays as it was
  }
}

// builds the whole challenge inside root and keeps it in step with the others
export function mountChallenge(root: HTMLElement): void {
  let sharing = false;
  let picture = "";

  const points = el("p", "hint chal-points");
  const streak = el("span", "hint chal-streak");
  const mode = el("div", "seg");
  const exercise = el("div", "stack");
  const title = el("b", "chal-title");
  const how = el("p", "chal-how");
  const meta = el("span", "hint");
  const linkKey = button(t("lesson"), "pill small", () => void call(CHANNELS.openExternal, linkKey.dataset.url || ""));
  const another = button("", "pill small", () => void act("exercise"));
  another.innerHTML = `${iconSvg("shuffle", 16)}<span>${t("another")}</span>`;
  exercise.append(title, how, meta, el("div", "row"));
  exercise.lastElementChild!.append(another, linkKey);
  const refBox = el("div", "stack");
  const refs = el("div", "chal-refs");
  const refEmpty = el("p", "empty", t("no references to pick from yet"));
  const reroll = button("", "pill small", () => void act("refs"));
  reroll.innerHTML = `${iconSvg("shuffle", 16)}<span>${t("other three")}</span>`;
  refBox.append(refs, refEmpty, reroll);
  // gesture drawing: start a timed session from here, draw, come back and finish
  const gestureBox = el("div", "stack");
  const gestureText = el("p", "chal-how");
  const gestureStatus = el("span", "hint");
  const gestureKeys = el("div", "row");
  gestureKeys.append(...GESTURE_PRESETS.map(([seconds, poses], index) => {
    const label = seconds >= 60 ? `${poses} × ${seconds / 60}m` : `${poses} × ${seconds}s`;
    return button(label, `pill small${index === 1 ? " accent" : ""}`, () => startGesture(seconds, poses));
  }));
  gestureBox.append(el("b", "chal-title", t("gesture drawing")), gestureText, gestureKeys, gestureStatus);
  const footer = el("div", "stack chal-footer");

  root.classList.add("chal");
  root.replaceChildren(streak, points, mode, exercise, refBox, gestureBox, footer);

  const paintShare = (done: boolean): HTMLElement => {
    const box = el("div", "stack chal-share");
    const picker = el("label", "pill chal-pick");
    picker.innerHTML = `${iconSvg("camera", 16)}<span>${picture ? t("change the drawing") : t("add a screenshot or photo of your drawing")}</span>`;
    const input = el("input");
    input.type = "file";
    input.accept = "image/*";
    input.hidden = true;
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (!file) return;
      pickImage(file).then((data) => {
        picture = data;
        paint();
      }, problem);
    });
    picker.append(input);
    box.append(picker);
    if (picture) {
      const preview = el("img", "chal-preview");
      preview.src = picture;
      preview.alt = t("your drawing");
      box.append(preview);
    }
    const note = el("input");
    note.placeholder = t("a line about it (optional)");
    note.maxLength = 140;
    note.autocomplete = "off";
    note.setAttribute("aria-label", t("a line about it (optional)"));
    box.append(note);
    const row = el("div", "row");
    const post = button(t("share on the feed, +{n} points", { n: CHALLENGE_POINTS.drawing }), "pill accent", () => {
      if (!picture) return flashToast(t("add your drawing first"));
      void act(done ? "post" : "done", "", { image: picture, note: note.value }).then((ok) => {
        if (ok) {
          sharing = false;
          picture = "";
          paint();
        }
      });
    });
    row.append(post);
    if (!done) row.append(button(t("finish without sharing"), "pill", () => void act("done")));
    row.append(button(t("cancel"), "pill small", () => {
      sharing = false;
      paint();
    }));
    box.append(row);
    return box;
  };

  const paint = (): void => {
    if (!state) return;
    const current = state;
    const exerciseMode = current.mode === "exercise";
    const gestureMode = current.mode === "gesture";
    points.textContent = current.done
      ? current.posted ? t("you drew today and shared it, nice") : t("you can still share your drawing for +{n} points", { n: CHALLENGE_POINTS.drawing })
      : t("worth +{done} points, and +{more} more when you share your drawing, plus any achievement it unlocks", { done: CHALLENGE_POINTS.done, more: CHALLENGE_POINTS.drawing });
    streak.textContent = current.streak ? t("{n} day streak", { n: current.streak }) : "";
    mode.hidden = current.done;
    mode.replaceChildren(...(["exercise", "ref", "gesture"] as const).map((value) => {
      const option = button(t(value === "ref" ? "draw a ref" : value === "gesture" ? "gesture" : "exercise"), `seg-option${current.mode === value ? " active" : ""}`, () => void act("mode", value));
      return option;
    }));
    exercise.hidden = !exerciseMode;
    refBox.hidden = current.mode !== "ref";
    gestureBox.hidden = !gestureMode;
    gestureText.textContent = t("pick a length, draw the poses, then come back here and press done");
    gestureStatus.textContent = current.gesture.sessions > 0 ? t("session started today, {n} poses picked", { n: current.gesture.poses }) : t("no gesture session started today yet");
    gestureKeys.hidden = current.done;
    title.textContent = t(current.exercise.title);
    how.textContent = t(current.exercise.how);
    meta.textContent = `${t("{n} min", { n: current.exercise.minutes })} · ${t(current.exercise.source)}`;
    linkKey.hidden = !current.exercise.link;
    linkKey.dataset.url = current.exercise.link;
    another.hidden = current.done;
    refs.replaceChildren(...current.refs.map((ref) => {
      const chosen = current.picked === ref.id;
      const cell = el("div", "chal-ref-cell");
      // the picture opens big, choosing it is its own button underneath
      const look = el("button", `chal-ref${chosen ? " picked" : ""}`);
      look.type = "button";
      look.setAttribute("aria-label", t("open the reference"));
      const image = el("img");
      image.src = mediaSrc(ref.thumb);
      image.alt = "";
      image.loading = "lazy";
      look.append(image);
      look.addEventListener("click", () => openLightbox(mediaSrc(ref.image || ref.thumb), current.done ? [] : [{
        label: chosen ? t("don't draw this one") : t("draw this one"),
        accent: !chosen,
        run: () => void act("pick", ref.id)
      }]));
      const choose = button(chosen ? t("picked") : t("pick"), `pill small${chosen ? " accent" : ""}`, () => void act("pick", ref.id));
      choose.setAttribute("aria-pressed", String(chosen));
      choose.disabled = current.done;
      cell.append(look, choose);
      return cell;
    }));
    refEmpty.hidden = current.refs.length > 0;
    reroll.hidden = current.done || current.refs.length === 0;
    footer.replaceChildren();
    if (current.image) {
      const shown = el("img", "chal-preview");
      shown.src = mediaSrc(current.image);
      shown.alt = t("your drawing");
      footer.append(shown);
    }
    if (sharing) footer.append(paintShare(current.done));
    else if (!current.done) {
      footer.append(button(t("done, I drew it"), "pill accent", () => {
        sharing = true;
        paint();
      }));
    } else if (!current.posted) {
      footer.append(button(t("add your drawing"), "pill", () => {
        sharing = true;
        paint();
      }));
    }
  };
  mounts.push(paint);
  paint();
}

export function setupChallengeModule(): void {
  mountChallenge(document.getElementById("chal-home")!);
  mountChallenge(document.getElementById("chal-view")!);
  document.getElementById("chal-open")?.addEventListener("click", () => showView("challenge"));
  onViewShown("today", () => void loadChallenge());
  onViewShown("challenge", () => void loadChallenge());
  void loadChallenge();
}
